'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useSupabaseAuth } from './SupabaseAuthContext';

export type Message = {
  id: number;
  text: string;
  sender: 'user' | 'ai';
};

type ChatContextType = {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  sendMessage: (text: string) => void;
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  clearChat: () => void;
  isTyping: boolean;
  // Free messages left in the rolling 24h window for anonymous users.
  // null when signed-in (uncapped) or not yet known.
  remainingMessages: number | null;
  isLimitReached: boolean;
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider = ({ children }: { children: ReactNode }) => {
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasRecommendedCoaching, setHasRecommendedCoaching] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  // The server (/api/chat/claude) is the source of truth: it enforces the real
  // cap (10 messages per rolling 24h, counted by IP) and reports how many remain.
  // These just mirror what it says so the UI can show an honest counter and limit
  // screen — they are not the enforcement (which can't be bypassed client-side).
  const [remainingMessages, setRemainingMessages] = useState<number | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  const { user, session } = useSupabaseAuth();

  const isLimitReached = !user && limitReached;

  const openChat = () => setIsChatOpen(true);
  const closeChat = () => setIsChatOpen(false);
  const clearChat = () => {
    setMessages([]);
    setHasRecommendedCoaching(false);
    setIsTyping(false);
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;

    // Add the user's message immediately.
    const userMessage: Message = { id: Date.now(), text, sender: 'user' };
    setMessages((prev) => [...prev, userMessage]);

    // If the server already told us the anonymous limit is used up, show the
    // reset message instantly instead of making a call that will be rejected.
    if (!user && (limitReached || remainingMessages === 0)) {
      setLimitReached(true);
      const limitMessage: Message = {
        id: Date.now() + 1,
        text: "You've used all your free messages for now. They'll reset within 24 hours, or you can create a free account to keep chatting with me with no limit.",
        sender: 'ai',
      };
      setMessages((prev) => [...prev, limitMessage]);
      return;
    }

    setIsTyping(true);

    try {
      // Count user messages (excluding welcome message)
      const userMessageCount = messages.filter((msg) => msg.sender === 'user').length + 1;

      // Send the access token so the server can verify auth (the browser client
      // stores its session in localStorage, not cookies).
      const response = await fetch('/api/chat/claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : {}),
        },
        body: JSON.stringify({
          messages: [...messages, userMessage],
          userMessageCount,
          hasRecommendedCoaching,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Daily cap reached: flip the limit UI. (A transient "slow down" 429 has no
        // signupRequired flag, so it just shows its message without locking the UI.)
        if (data?.signupRequired) {
          setLimitReached(true);
          setRemainingMessages(0);
        }
        const errorText =
          data?.error ||
          "I apologize, but I'm having trouble responding right now. Please try again in a moment.";
        setMessages((prev) => [...prev, { id: Date.now() + 1, text: errorText, sender: 'ai' }]);
        return;
      }

      // Reflect the server's remaining-message count for anonymous users.
      if (!user && typeof data.remaining === 'number') {
        setRemainingMessages(data.remaining);
        if (data.remaining <= 0) setLimitReached(true);
      }

      // Add Claude's response
      const aiResponse: Message = {
        id: Date.now() + 1,
        text: data.message,
        sender: 'ai',
      };
      setMessages((prev) => [...prev, aiResponse]);

      // Track if coaching was recommended in this response
      if (data.recommendedCoaching) {
        setHasRecommendedCoaching(true);
      }
    } catch (error) {
      console.error('Chat error:', error);

      // Add error message
      const errorResponse: Message = {
        id: Date.now() + 1,
        text: "I apologize, but I'm having trouble responding right now. Please try again in a moment, or feel free to book a coaching call with Aubree for personalized support.",
        sender: 'ai',
      };
      setMessages((prev) => [...prev, errorResponse]);
    } finally {
      setIsTyping(false); // Hide typing indicator
    }
  };

  return (
    <ChatContext.Provider
      value={{
        messages,
        setMessages,
        sendMessage,
        isChatOpen,
        openChat,
        closeChat,
        clearChat,
        isTyping,
        // Never show a remaining-message counter to signed-in (uncapped) users.
        remainingMessages: user ? null : remainingMessages,
        isLimitReached,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => {
  const context = useContext(ChatContext);
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

export default ChatProvider;
